import React from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import ContactForm from "@/components/forms/ContactForm";
import { COMPANY } from "@/data/site";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      {/* HEADER */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
            Contact us
          </span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black text-[#0F172A] tracking-tight leading-[1.05]">
            Let's solve your plumbing problem.
          </h1>
          <p className="mt-5 max-w-2xl text-base lg:text-lg text-slate-600">
            Reach out by phone, email, or the form below. For emergencies,
            calling is fastest.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 py-16 lg:py-24" data-testid="contact-main-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* INFO */}
          <div className="lg:col-span-2 space-y-8">
            <InfoCard
              icon={Phone}
              label="Call us"
              testId="contact-info-phone"
              primary={
                <a
                  href={`tel:${COMPANY.phoneTel}`}
                  data-testid="contact-info-phone-link"
                  className="hover:text-[#0284C7]"
                >
                  {COMPANY.phone}
                </a>
              }
              secondary="Fastest for emergencies"
            />
            <InfoCard
              icon={Mail}
              label="Email"
              testId="contact-info-email"
              primary={
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="contact-info-email-link"
                  className="hover:text-[#0284C7] break-all"
                >
                  {COMPANY.email}
                </a>
              }
              secondary="We reply within one business day"
            />
            <InfoCard
              icon={MapPin}
              label="Address"
              testId="contact-info-address"
              primary={COMPANY.address}
              secondary={COMPANY.serviceArea}
            />
            <div
              data-testid="contact-info-hours"
              className="bg-white border border-slate-200 p-6"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-[#0284C7] text-white">
                  <Clock className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div className="text-xs uppercase tracking-[0.12em] font-semibold text-[#475569]">
                  Business hours
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {COMPANY.hours.map((h, i) => (
                  <li
                    key={i}
                    data-testid={`hours-row-${i}`}
                    className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 border-b border-slate-100 pb-2 last:border-b-0 last:pb-0"
                  >
                    <span className="font-semibold text-[#0F172A]">{h.day}</span>
                    <span className="text-slate-600">{h.time}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-slate-500">
                Emergency fee applies for after-hours and weekend service.
              </p>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-3">
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#0F172A]">
              Send us a message
            </h2>
            <p className="text-slate-600 mt-2">
              We'll get back to you quickly during business hours.
            </p>
            <div className="mt-6">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-white" data-testid="contact-map-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#0F172A]">
            Find us
          </h2>
          <p className="text-slate-600 mt-2">{COMPANY.address}</p>
          <div className="mt-6 border border-slate-200 overflow-hidden">
            <iframe
              data-testid="contact-map-iframe"
              title="Boston Express Plumbing location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(
                COMPANY.address
              )}&output=embed`}
              width="100%"
              height="420"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0 }}
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, label, primary, secondary, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 p-6">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center bg-[#0284C7] text-white">
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <div className="text-xs uppercase tracking-[0.12em] font-semibold text-[#475569]">
          {label}
        </div>
      </div>
      <div className="mt-3 font-display text-lg font-semibold text-[#0F172A]">
        {primary}
      </div>
      <div className="mt-1 text-sm text-slate-500">{secondary}</div>
    </div>
  );
}
